/**
 * Website Make changes — the site-wide panels (Header, Footer, Website style)
 * and the dialogs (Add a section, New page). Every change here is stored at
 * once as its own undo step, like a direct gesture in the transaction mode;
 * Done closes the panel.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  AnchoredPanel, CenterDialog, ChipGroup, PanelHeader, PanelNote, PanelText, VIOLET, VIOLET_LINE, VioletButton,
} from '@twasta/page-runtime/customize'
import { SECTION_TEMPLATES, TEMPLATE_CATEGORIES } from '@/pages/designers/page/sectionTemplates'
import type { Block, BlockType, Section } from '@/pages/designers/page/types'
import { makeBlock } from '@/pages/designers/page/blocks'
import {
  applyPageTheme, createPageTheme, listPageThemes, updatePageTheme, type ThemeOverview,
} from '@/api/pageThemes'
import type { SiteEditSite } from './api'
import type { SiteEditSession } from './session'
import { ADD_BLOCKS, contrast, FONTS, HEADER_SCROLL, PAGE_STARTS, renewIds, SWATCHES, toggleMenuPage } from './pageOps'

const muted = { fontSize: 13, color: 'var(--color-text-secondary, #6b7280)' } as const

function PanelFooter({ onDone, note = 'Each change saves on its own' }: { onDone: () => void; note?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end', paddingTop: 6, borderTop: '1px solid var(--color-border, #f1f3f6)' }}>
      <span style={muted}>{note}</span>
      <VioletButton hook="site-edit-panel-done" onClick={onDone}>Done</VioletButton>
    </div>
  )
}

// ── Header ───────────────────────────────────────────────────────────────

export function HeaderPanel({ anchor, site, session, saveSettings, designerHref, onClose }: {
  anchor: HTMLElement | null
  site: SiteEditSite
  session: SiteEditSession
  saveSettings: (label: string, set: Record<string, unknown>, before: Record<string, unknown>) => Promise<boolean>
  designerHref: (obj: string) => string
  onClose: () => void
}) {
  const s = site.settings
  const pages = site.pages.filter((p) => !p.chrome)
  return (
    <AnchoredPanel anchor={anchor} onClose={onClose} label="Header" width={400}>
      <PanelHeader title="Header" sub="On every page of this website" />
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <PanelText label="Website name" value={s.title || site.node_title}
          onCommit={(v) => void saveSettings('Renamed the website', { title: v }, { title: s.title })} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Pages in the menu</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {pages.map((p) => (
              <button key={p.obj_name} type="button" role="switch" aria-checked={p.in_menu}
                data-tw-action={`site-edit-menu:${p.obj_name}`}
                onClick={() => void saveSettings(
                  `${p.in_menu ? 'Took' : 'Put'} “${p.title}” ${p.in_menu ? 'out of' : 'in'} the menu`,
                  { menus: toggleMenuPage(site, p.obj_name, !p.in_menu) }, { menus: s.menus || {} })}
                disabled={session.status === 'saving'}
                style={{
                  minHeight: 30, padding: '0 12px', borderRadius: 15, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5,
                  border: `1px solid ${p.in_menu ? VIOLET : VIOLET_LINE}`, background: p.in_menu ? VIOLET : '#fff',
                  color: p.in_menu ? '#fff' : '#4c1d95', fontWeight: p.in_menu ? 600 : 500,
                }}>{p.in_menu ? '✓ ' : '+ '}{p.title}</button>
            ))}
          </div>
          <span style={muted}>The footer lists the same pages.</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>When visitors scroll</span>
          <ChipGroup label="When visitors scroll" hook="site-edit-scroll"
            options={HEADER_SCROLL.map((h) => ({ value: h.id, label: h.label }))}
            value={s.header_scroll}
            onChange={(v) => void saveSettings('Changed how the header scrolls', { header_scroll: v }, { header_scroll: s.header_scroll })} />
        </div>
        {s.header_page_obj_name && (
          <PanelNote>
            This website’s header is a page of its own. Change its logo and layout in the{' '}
            <a href={designerHref(s.header_page_obj_name)} target="_top" rel="noreferrer" style={{ color: VIOLET, fontWeight: 600 }}>page designer</a>.
          </PanelNote>
        )}
        <PanelFooter onDone={onClose} />
      </div>
    </AnchoredPanel>
  )
}

export function FooterPanel({ anchor, site, designerHref, onClose }: {
  anchor: HTMLElement | null; site: SiteEditSite; designerHref: (obj: string) => string; onClose: () => void
}) {
  const footer = site.settings.footer_page_obj_name
  return (
    <AnchoredPanel anchor={anchor} onClose={onClose} label="Footer" width={380}>
      <PanelHeader title="Footer" sub="On every page of this website" />
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {footer
          ? <PanelNote>
              This website’s footer is a page of its own. Change it in the{' '}
              <a href={designerHref(footer)} target="_top" rel="noreferrer" style={{ color: VIOLET, fontWeight: 600 }}>page designer</a>.
            </PanelNote>
          : <PanelNote>The footer shows the website’s name and the pages in the menu. Change those from the header’s ⚙.</PanelNote>}
        <PanelFooter onDone={onClose} note="" />
      </div>
    </AnchoredPanel>
  )
}

// ── Website style ────────────────────────────────────────────────────────

function darker(hex: string, by = 0.12): string {
  const h = hex.replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16)
  if (Number.isNaN(n)) return hex
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v * (1 - by)))
  return `#${ch.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

interface Look { id: string; name: string; kind: 'project' | 'preset'; bg: string; heading: string; primary: string; onPrimary: string; font?: string; heading_font?: string; sites: string[] }

export function StylePanel({ anchor, project, site, session, onApplied, onClose }: {
  anchor: HTMLElement | null
  project: string
  site: SiteEditSite
  session: SiteEditSession
  /** A theme change re-themes every page on the server: re-read them. */
  onApplied: () => void
  onClose: () => void
}) {
  const [overview, setOverview] = useState<ThemeOverview | null>(null)
  const [loadError, setLoadError] = useState('')
  const reload = () => listPageThemes(project)
    .then((o) => { setOverview(o); setLoadError('') })
    .catch((e) => { console.warn('[site-edit] themes could not load', e); setLoadError('The styles could not be loaded. Close this and try again.') })
  useEffect(() => { void reload() }, [project]) // eslint-disable-line react-hooks/exhaustive-deps

  const looks: Look[] = useMemo(() => {
    if (!overview) return []
    const pal = (t: unknown) => ((t as { palette?: Record<string, string> } | undefined)?.palette || {}) as Record<string, string>
    return [
      ...overview.themes.map((t) => ({
        id: t.id, name: t.name, kind: 'project' as const,
        bg: pal(t.theme).background || '#fff', heading: pal(t.theme).heading || '#111', primary: pal(t.theme).primary || '#333',
        onPrimary: pal(t.theme).onPrimary || '#fff',
        font: t.theme.typography?.fontFamily, heading_font: t.theme.typography?.headingFamily,
        sites: t.usage?.sites || [],
      })),
      ...overview.presets.map((p) => ({
        id: p.id, name: p.label, kind: 'preset' as const,
        bg: pal(p.theme).background || '#fff', heading: pal(p.theme).heading || '#111', primary: pal(p.theme).primary || '#333',
        onPrimary: pal(p.theme).onPrimary || '#fff',
        font: p.theme.typography?.fontFamily, heading_font: p.theme.typography?.headingFamily, sites: [],
      })),
    ]
  }, [overview])

  const currentId = site.settings.theme?.ref || site.settings.theme?.preset || ''
  const current = looks.find((l) => l.id === currentId)
  const apply = async (id: string) => {
    await applyPageTheme(project, { theme: id, site: site.obj_name })
    onApplied()
    await reload()
  }
  const pick = (l: Look) => {
    if (l.id === currentId) return
    const before = currentId
    void session.record(`Website style: ${l.name}`, () => apply(l.id), async () => { if (before) await apply(before) })
  }

  /** Change the site's look. A built-in look is first copied into a theme of
   *  this website's own, so no other website changes with it. */
  const tweak = (label: string, patch: Record<string, unknown>, beforePatch: Record<string, unknown>) => {
    if (!current) return
    const startedOn = current
    let made = ''
    void session.record(label, async () => {
      let id = startedOn.kind === 'project' ? startedOn.id : made
      if (!id) {
        const created = await createPageTheme(project, { name: `${site.title || site.node_title} style`, from_preset: startedOn.id })
        made = created.id
        id = created.id
        await applyPageTheme(project, { theme: id, site: site.obj_name })
      } else if (startedOn.kind === 'preset') {
        await applyPageTheme(project, { theme: id, site: site.obj_name })
      }
      await updatePageTheme(project, id, { theme: patch as never })
      onApplied()
      await reload()
    }, async () => {
      if (startedOn.kind === 'preset') await apply(startedOn.id)
      else { await updatePageTheme(project, startedOn.id, { theme: beforePatch as never }); onApplied(); await reload() }
    })
  }

  const doc = overview?.themes.find((t) => t.id === currentId)
  const palette = (doc?.theme.palette || overview?.presets.find((p) => p.id === currentId)?.theme.palette || {}) as Record<string, string>
  const primary = palette.primary || current?.primary || '#333333'
  const onPrimary = palette.onPrimary || '#ffffff'
  const fontNow = FONTS.find((f) => f.body === current?.font && f.heading === current?.heading_font)?.id || ''
  const shared = current?.kind === 'project' && current.sites.filter((s) => s !== site.obj_name).length > 0
  const setColour = (hex: string) => tweak('Changed the main colour',
    { palette: { primary: hex, primaryHover: darker(hex) } },
    { palette: { primary: palette.primary, primaryHover: palette.primaryHover } })

  return (
    <AnchoredPanel anchor={anchor} onClose={onClose} label="Website style" width={440}>
      <PanelHeader title="Website style" sub="Colours and fonts for every page of this website" />
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {loadError && <PanelNote tone="warn">{loadError}</PanelNote>}
        {!overview && !loadError && <span style={muted}>Loading the styles…</span>}
        {overview && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Style</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
                {looks.map((l) => {
                  const on = l.id === currentId
                  return (
                    <button key={`${l.kind}:${l.id}`} type="button" aria-pressed={on} onClick={() => pick(l)}
                      data-tw-action={`site-edit-style:${l.id}`} disabled={session.status === 'saving'}
                      style={{
                        display: 'flex', flexDirection: 'column', gap: 6, padding: 8, borderRadius: 10, textAlign: 'left',
                        cursor: 'pointer', fontFamily: 'inherit', color: 'inherit',
                        border: on ? `2px solid ${VIOLET}` : '1px solid var(--color-border, #dde3ea)', background: on ? '#faf8ff' : '#fff',
                      }}>
                      <span aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: 9, borderRadius: 6, background: l.bg, border: '1px solid rgba(0,0,0,.08)' }}>
                        <span style={{ display: 'block', height: 8, width: '70%', borderRadius: 3, background: l.heading }} />
                        <span style={{ display: 'block', height: 14, width: '44%', borderRadius: 7, background: l.primary }} />
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <strong style={{ flex: 1, fontSize: 13.5 }}>{l.name}</strong>
                        {on && <span style={{ color: VIOLET, fontWeight: 700 }}>✓</span>}
                      </span>
                      <span style={muted}>{l.kind === 'project' ? 'Made for this application' : 'Ready-made'}</span>
                    </button>
                  )
                })}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Main colour</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                {[primary, ...SWATCHES.filter((s) => s.toLowerCase() !== primary.toLowerCase())].map((hex) => {
                  const on = hex.toLowerCase() === primary.toLowerCase()
                  return (
                    <button key={hex} type="button" aria-label={`Use the colour ${hex}`} aria-pressed={on}
                      disabled={!current || session.status === 'saving'} onClick={() => { if (!on) setColour(hex) }}
                      style={{
                        width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', background: hex, padding: 0,
                        border: on ? '3px solid #1c2430' : '1px solid rgba(0,0,0,.15)', boxShadow: on ? '0 0 0 2px #fff inset' : 'none',
                      }} />
                  )
                })}
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...muted }}>Other
                  <input type="color" value={/^#[0-9a-f]{6}$/i.test(primary) ? primary : '#333333'}
                    onChange={(e) => setColour(e.target.value)} disabled={!current}
                    style={{ width: 36, height: 30, padding: 0, border: '1px solid #c7cfd9', borderRadius: 6, background: '#fff' }} />
                </label>
              </div>
              {contrast(primary, onPrimary) < 4.5 && (
                <PanelNote tone="warn">Text on this colour is hard to read ({contrast(primary, onPrimary).toFixed(1)} : 1). Pick a darker colour.</PanelNote>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Font</span>
              <ChipGroup label="Font" hook="site-edit-font" disabled={!current || session.status === 'saving'}
                options={FONTS.map((f) => ({ value: f.id, label: f.label }))} value={fontNow}
                onChange={(id) => {
                  const f = FONTS.find((x) => x.id === id)
                  if (!f) return
                  tweak(`Font: ${f.label}`, { typography: { fontFamily: f.body, headingFamily: f.heading } },
                    { typography: { fontFamily: current?.font, headingFamily: current?.heading_font } })
                }} />
            </div>
            <PanelNote>
              {current?.kind === 'preset'
                ? 'A colour or font change keeps a copy of this style for this website, so nothing else changes.'
                : shared
                  ? `The “${current?.name}” style is used by other websites too — a colour or font change changes them as well.`
                  : 'Every page of this website uses this style, header and footer included.'}
            </PanelNote>
          </>
        )}
        <PanelFooter onDone={onClose} />
      </div>
    </AnchoredPanel>
  )
}

// ── Add a section ────────────────────────────────────────────────────────

export function AddSectionDialog({ where, onAdd, onClose }: {
  where: string; onAdd: (sec: Section) => void; onClose: () => void
}) {
  const [cat, setCat] = useState<string>('All')
  const [picked, setPicked] = useState<string>('')
  const shown = SECTION_TEMPLATES.filter((t) => cat === 'All' || t.category === cat)
  const tpl = SECTION_TEMPLATES.find((t) => t.id === picked)
  return (
    <CenterDialog title="Add a section" onClose={onClose} width={780} hook="site-edit-add-section"
      footer={<>
        <VioletButton quiet onClick={onClose}>Cancel</VioletButton>
        <VioletButton hook="site-edit-add-section-confirm" disabled={!tpl}
          onClick={() => { if (tpl) onAdd(renewIds(tpl.build())) }}>Add section</VioletButton>
      </>}>
      <span style={{ fontWeight: 600, fontSize: 15 }}>What should it show?</span>
      <ChipGroup label="Kind of section" hook="site-edit-section-kind"
        options={['All', ...TEMPLATE_CATEGORIES].map((c) => ({ value: c, label: c }))} value={cat} onChange={setCat} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
        {shown.map((t) => {
          const on = t.id === picked
          return (
            <button key={t.id} type="button" aria-pressed={on} onClick={() => setPicked(t.id)}
              onDoubleClick={() => onAdd(renewIds(t.build()))}
              data-tw-action={`site-edit-template:${t.id}`}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, padding: '10px 12px',
                borderRadius: 10, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: 'inherit',
                border: on ? `2px solid ${VIOLET}` : '1px solid var(--color-border, #dde3ea)', background: on ? '#faf8ff' : '#fff',
              }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: VIOLET }}>{t.category}</span>
              <strong style={{ fontSize: 14 }}>{t.name}</strong>
              <span style={muted}>{t.description}</span>
            </button>
          )
        })}
      </div>
      <span style={muted}>{where}. It takes the website’s style; change its words from its ⚙ afterwards.</span>
    </CenterDialog>
  )
}

// ── Add a block ──────────────────────────────────────────────────────────

/** A new block from a sitePolicy.json `addBlocks` entry: the page designer's
 *  own new block with the words it starts with, every id fresh. */
export function newBlock(type: string): Block {
  const choice = ADD_BLOCKS.find((b) => b.type === type)
  return renewIds({ ...makeBlock(type as BlockType), ...(choice?.set || {}) } as Block)
}

export function AddBlockDialog({ where, onAdd, onClose }: {
  where: string; onAdd: (block: Block) => void; onClose: () => void
}) {
  return (
    <CenterDialog title="Add a block" onClose={onClose} width={620} hook="site-edit-add-block"
      footer={<VioletButton quiet onClick={onClose}>Cancel</VioletButton>}>
      <span style={{ fontWeight: 600, fontSize: 15 }}>What should it be?</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
        {ADD_BLOCKS.map((b) => (
          <button key={b.type} type="button" onClick={() => onAdd(newBlock(b.type))}
            data-tw-action={`site-edit-block-type:${b.type}`}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, padding: '10px 12px',
              borderRadius: 10, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: 'inherit',
              border: '1px solid var(--color-border, #dde3ea)', background: '#fff',
            }}>
            <strong style={{ fontSize: 14, color: VIOLET }}>{b.label}</strong>
            <span style={muted}>{b.description}</span>
          </button>
        ))}
      </div>
      <span style={muted}>{where}. Click its words on the page to change them.</span>
    </CenterDialog>
  )
}

// ── New page ─────────────────────────────────────────────────────────────

export function NewPageDialog({ onAdd, onClose }: {
  onAdd: (body: { title: string; start: string; in_menu: boolean }) => Promise<string | null>
  onClose: () => void
}) {
  const [title, setTitle] = useState('')
  const [start, setStart] = useState(PAGE_STARTS[0]?.id || 'text')
  const [inMenu, setInMenu] = useState<'yes' | 'no'>('yes')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const add = async () => {
    if (!title.trim() || busy) return
    setBusy(true)
    setError('')
    const err = await onAdd({ title: title.trim(), start, in_menu: inMenu === 'yes' })
    setBusy(false)
    if (err) setError(err)
  }
  return (
    <CenterDialog title="Add a page" onClose={onClose} width={520} hook="site-edit-new-page"
      footer={<>
        <VioletButton quiet onClick={onClose}>Cancel</VioletButton>
        <VioletButton hook="site-edit-new-page-confirm" disabled={!title.trim() || busy} onClick={() => void add()}>
          {busy ? 'Adding…' : 'Add page'}
        </VioletButton>
      </>}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>What is the page called?</span>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Services"
          onKeyDown={(e) => { if (e.key === 'Enter') void add() }} data-tw-field="site-edit-page-name"
          style={{ height: 40, borderRadius: 6, padding: '0 10px', border: '1px solid var(--color-border, #d5dae1)', fontSize: 15, fontFamily: 'inherit' }} />
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontWeight: 600 }}>It starts with</span>
        <ChipGroup label="It starts with" hook="site-edit-page-start"
          options={PAGE_STARTS.map((p) => ({ value: p.id, label: p.label }))} value={start} onChange={setStart} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontWeight: 600 }}>Show it in the menu</span>
        <ChipGroup label="Show it in the menu" hook="site-edit-page-menu"
          options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={inMenu} onChange={setInMenu} />
      </div>
      {error && <PanelNote tone="warn">{error}</PanelNote>}
    </CenterDialog>
  )
}
